/** Find a property by NAME, not by exact address (field feedback, 2026-07-25).
 *
 *  Maricopa County publishes the recorded subdivision plats as a public layer.
 *  Typing "greenway parc 3" returns the real platted boundary — authoritative,
 *  no scraping, no Google. Outside Maricopa this simply returns nothing and the
 *  UI falls back to address search or hand-drawing.
 */
import { z } from 'zod';
import { fetchJson, ServiceError } from '../core/net';
import { netRingsAreaSqFt, perimeterFt, type LatLng } from '../core/geo';
import { nowIso } from '../core/ids';
import type { ParcelResult } from '../core/types';

const BASE = 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Subdivisions/MapServer/0/query';

export interface SubdivisionHit {
  name: string;        // "GREENWAY PARC AT SURPRISE 3 AMD"
  mcr: string;         // "543-19" — the recorded plat number
  book?: string;
  page?: string;
}

/**
 * ArcGIS `where` takes SQL. User text must never reach it unescaped (§8 input handling).
 * Allow only characters that legitimately appear in plat names, double any quote,
 * and cap the length.
 */
export function sanitizeSubdivisionQuery(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9 &.'#/-]/g, ' ')   // everything else becomes a space
    .replace(/'/g, "''")                  // SQL-escape quotes
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
}

const ListSchema = z.object({
  features: z.array(z.object({
    attributes: z.object({
      subname: z.string().nullish(),
      mcrnum: z.string().nullish(),
      mcr_book: z.string().nullish(),
      mcr_page: z.string().nullish(),
    }),
  })),
}).loose();

const GeomSchema = z.object({
  features: z.array(z.object({
    attributes: z.object({ subname: z.string().nullish(), mcrnum: z.string().nullish() }),
    geometry: z.object({ rings: z.array(z.array(z.tuple([z.number(), z.number()]).rest(z.number()))) }).nullish(),
  })),
}).loose();

/**
 * Words, not one blob.
 *
 * The plat is recorded as "GREENWAY PARC AT SURPRISE 3 AMD". A single
 * `LIKE '%GREENWAY PARC 3%'` cannot match that string — the words are not
 * adjacent — so typing the community's ordinary name returned "no recorded
 * plat" for a plat that plainly exists. Field-confirmed 2026-07-25.
 *
 * Each word becomes its own LIKE, ANDed together, so word order and anything
 * the county inserted in between stop mattering.
 */
export function buildSubdivisionWhere(tokens: string[]): string {
  return tokens.map((t) => `UPPER(subname) LIKE '%${t}%'`).join(' AND ');
}

/** "greenway parc 3, surprise az" → ['GREENWAY','PARC','3','SURPRISE','AZ'] */
export function subdivisionTokens(raw: string): string[] {
  return sanitizeSubdivisionQuery(raw)
    .split(' ')
    .map((t) => t.replace(/[^A-Z0-9&'.#/-]/g, ''))
    .filter((t) => t.length > 0 && !/^(AZ|ARIZONA|USA|US)$/.test(t) && !/^\d{5}$/.test(t))
    .slice(0, 8);
}

async function querySubdivisions(where: string): Promise<SubdivisionHit[]> {
  const params = new URLSearchParams({
    where,
    outFields: 'subname,mcrnum,mcr_book,mcr_page',
    returnGeometry: 'false',
    resultRecordCount: '25',
    f: 'json',
  });
  const raw = await fetchJson('Maricopa County subdivisions', `${BASE}?${params.toString()}`);
  const parsed = ListSchema.safeParse(raw);
  if (!parsed.success) throw new ServiceError('Maricopa County subdivisions', 'malformed', 'The county answered in an unexpected shape.');
  const seen = new Set<string>();
  const hits: SubdivisionHit[] = [];
  for (const f of parsed.data.features) {
    const name = (f.attributes.subname ?? '').trim();
    const mcr = (f.attributes.mcrnum ?? '').trim();
    if (!name || seen.has(`${name}|${mcr}`)) continue;
    seen.add(`${name}|${mcr}`);
    hits.push({ name, mcr, book: f.attributes.mcr_book ?? undefined, page: f.attributes.mcr_page ?? undefined });
  }
  // shortest names first: "GREENWAY PARC AT SURPRISE 3" before "... 3 AMD REPLAT"
  return hits.sort((a, b) => a.name.length - b.name.length || a.name.localeCompare(b.name));
}

/** Name search. Returns [] when nothing matches — an answer, not an error.
 *  Extra words are dropped from the end until something matches, so a trailing
 *  city or a stray word does not turn a real community into a dead end. */
export async function searchSubdivisions(query: string): Promise<SubdivisionHit[]> {
  const tokens = subdivisionTokens(query);
  if (!tokens.length || tokens.join('').length < 3) return [];
  for (let n = tokens.length; n >= 1; n--) {
    const hits = await querySubdivisions(buildSubdivisionWhere(tokens.slice(0, n)));
    if (hits.length) return hits;
    if (n <= 2) break;   // below two words the matches stop meaning anything
  }
  return [];
}

/** Fetch the chosen plat's boundary and turn it into a ParcelResult the app already understands. */
export async function loadSubdivisionBoundary(hit: SubdivisionHit): Promise<ParcelResult> {
  const mcr = sanitizeSubdivisionQuery(hit.mcr);
  const name = sanitizeSubdivisionQuery(hit.name);
  const where = mcr ? `mcrnum = '${mcr}'` : `UPPER(subname) = '${name}'`;
  const params = new URLSearchParams({
    where,
    outFields: 'subname,mcrnum',
    returnGeometry: 'true',
    outSR: '4326',
    f: 'json',
  });
  const raw = await fetchJson('Maricopa County subdivisions', `${BASE}?${params.toString()}`, { timeoutMs: 20000 });
  const parsed = GeomSchema.safeParse(raw);
  if (!parsed.success) throw new ServiceError('Maricopa County subdivisions', 'malformed', 'The county answered in an unexpected shape.');

  // a plat can be recorded as several polygons; keep them all
  const rings: LatLng[][] = [];
  for (const f of parsed.data.features) {
    for (const ring of f.geometry?.rings ?? []) {
      if (ring.length >= 3) rings.push(ring.map((pt) => [pt[1], pt[0]] as LatLng));
    }
  }
  if (!rings.length) {
    throw new ServiceError('Maricopa County subdivisions', 'empty', 'The county has no boundary on file for that plat. Draw the boundary by hand instead.');
  }
  return {
    provider: 'maricopa-subdivision',
    apn: '',
    label: `${hit.name}${hit.mcr ? ` · MCR ${hit.mcr}` : ''}`,
    address: undefined,
    rings,
    lotSqFt: Math.round(netRingsAreaSqFt(rings)),
    perimeterFt: Math.round(perimeterFt(rings[0] ?? [])),
    fetchedAt: nowIso(),
  };
}

function extent(rings: LatLng[][]): { s: number; w: number; n: number; e: number } {
  let s = 90, w = 180, n = -90, e = -180;
  for (const ring of rings) for (const [lat, lng] of ring) {
    if (lat < s) s = lat; if (lat > n) n = lat;
    if (lng < w) w = lng; if (lng > e) e = lng;
  }
  return { s, w, n, e };
}

/** center of a boundary, for pointing the map at it */
export function boundaryCenter(rings: LatLng[][]): LatLng {
  const { s, w, n, e } = extent(rings);
  return [(s + n) / 2, (w + e) / 2];
}

/** Zoom at which the whole boundary fits on screen. A fixed zoom either buried a
 *  small plat or hung a big one off both edges; either way the user has to fiddle
 *  with the map before doing any work, which is the thing they asked us to stop. */
export function boundaryZoom(rings: LatLng[][], panePx = { w: 720, h: 520 }): number {
  const { s, w, n, e } = extent(rings);
  const midLat = (s + n) / 2;
  const mPerDegLat = 111_320;
  const mPerDegLng = 111_320 * Math.cos((midLat * Math.PI) / 180);
  const widthM = Math.max((e - w) * mPerDegLng, 1);
  const heightM = Math.max((n - s) * mPerDegLat, 1);
  const worldRes = 156543.03392 * Math.cos((midLat * Math.PI) / 180); // m/px at zoom 0
  // 0.9 leaves a margin so the outline is not flush against the pane edge
  const byW = Math.log2((worldRes * panePx.w * 0.9) / widthM);
  const byH = Math.log2((worldRes * panePx.h * 0.9) / heightM);
  return Math.max(12, Math.min(20, Math.floor(Math.min(byW, byH))));
}
